/** Async search autocomplete for schedule parameter search-type inputs. */

import { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { api } from "@/services/api";

/** Search result option shape. */
export interface SearchOption {
  id: string;
  name: string;
}

export interface SearchAutocompleteProps {
  paramName: string;
  label: string;
  helpText?: string;
  searchEndpoint: string;
  value: SearchOption | null;
  onChange: (value: SearchOption | null) => void;
  required?: boolean;
  error?: string;
  credential?: string;
  pluginId?: string;
}

/**
 * Maps each search-type paramType to its backend search endpoint.
 */
export const SEARCH_PARAM_ENDPOINTS: Record<string, string> = {
  param_company_search: "/reporting/entities/search?type=accelo_company",
  param_project_search: "/reporting/entities/search?type=accelo_project",
  param_retainer_search: "/reporting/entities/search?type=accelo_retainer",
  param_contact_search: "/reporting/entities/search?type=accelo_contact",
  param_opportunity_search: "/reporting/entities/search?type=accelo_opportunity",
  param_staff_search: "/reporting/entities/search?type=accelo_staff",
  param_zendesk_org_search: "/reporting/entities/search?type=zendesk_organization",
  param_zendesk_user_search: "/reporting/entities/search?type=zendesk_user",
  param_datadog_monitor_search: "/reporting/entities/search?type=datadog_monitor",
  param_datadog_slo_search: "/reporting/entities/search?type=datadog_slo",
  param_datadog_host_search: "/reporting/entities/search?type=datadog_host",
};

/**
 * SearchAutocomplete — a reusable async autocomplete that queries the backend
 * as the user types. Debounces input at 300ms. Shows inline error on API failure
 * without blocking other fields.
 */
export default function SearchAutocomplete({
  paramName,
  label,
  helpText,
  searchEndpoint,
  value,
  onChange,
  required,
  error,
  credential,
  pluginId,
}: SearchAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      setFetchError("");
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setFetchError("");
      try {
        const separator = searchEndpoint.includes("?") ? "&" : "?";
        let url = `${searchEndpoint}${separator}q=${encodeURIComponent(inputValue)}`;
        if (pluginId) url += `&pluginId=${encodeURIComponent(pluginId)}`;
        if (credential) url += `&credential=${encodeURIComponent(credential)}`;
        const data = await api.get<{ results: SearchOption[] }>(url);
        setOptions(data.results ?? []);
      } catch {
        setOptions([]);
        setFetchError("Search unavailable");
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue, searchEndpoint, credential, pluginId]);

  const helperMessage = error || fetchError || helpText || "Type at least 2 characters to search";
  const hasError = !!error || !!fetchError;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      loading={loading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperMessage}
          error={hasError}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          inputProps={{
            ...params.inputProps,
            "data-testid": `search-autocomplete-${paramName}`,
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name} ({option.id})
        </li>
      )}
    />
  );
}
